"use client"

import Link from 'next/link';
import TheTime from './thetime';
import { Suspense, useState, useEffect } from 'react';
import { FaHeart, FaUmbrellaBeach } from 'react-icons/fa6';
import Logo from './ui/logo';

const Footer = () => {
    const [currentYear, setCurrentYear] = useState<number | null>(null);

    useEffect(() => {
        setCurrentYear(new Date().getFullYear());
    }, []);
    
    return (
        <footer className="relative -mt-px overflow-hidden bg-slate-900">
            <div className="relative mx-auto max-w-6xl px-6 pb-8 pt-12">
                {/* Main footer content */}
                <div className="grid gap-12 md:grid-cols-3">
                    {/* Brand section */}
                    <div className="flex flex-col items-center md:items-start">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-400/10">
                            <Logo className="h-10 w-10 fill-amber-400" />
                        </div>
                        <h3 className="mb-2 font-special text-xl font-bold text-white">
                            Stellplatz Zingst
                        </h3>
                        <p className="font-handwritten text-lg text-amber-300">
                            Freiheit hinterm Deich
                        </p>
                        <div className="mt-4 flex items-center gap-2 text-slate-400">
                            <FaUmbrellaBeach className="h-4 w-4 text-amber-400" />
                            <span className="text-sm">Direkt an der Ostsee</span>
                        </div>
                    </div>

                    {/* Links section */}
                    <div className="flex flex-col items-center md:items-start">
                        <h4 className="mb-4 font-semibold text-white">Navigation</h4>
                        <nav className="flex flex-col items-center gap-2 md:items-start">
                            <Link href="/" className="text-slate-400 transition-colors hover:text-amber-400">
                                Startseite
                            </Link>
                            <Link href="/#preise" className="text-slate-400 transition-colors hover:text-amber-400">
                                Preise & Bezahlung
                            </Link>
                            <Link href="/#anfahrt" className="text-slate-400 transition-colors hover:text-amber-400">
                                Anfahrt
                            </Link>
                            <Link href="/jobs" className="text-slate-400 transition-colors hover:text-amber-400">
                                Jobs
                            </Link>
                            <Link href="/kontakt" className="text-slate-400 transition-colors hover:text-amber-400">
                                Kontakt
                            </Link>
                        </nav>
                    </div>

                    {/* Legal section */}
                    <div className="flex flex-col items-center md:items-start">
                        <h4 className="mb-4 font-semibold text-white">Rechtliches</h4>
                        <nav className="flex flex-col items-center gap-2 md:items-start">
                            <Link 
                                href="http://www.kite-club.com/agb-wassersport-lifestyle-zingst.pdf" 
                                target="_blank"
                                className="text-slate-400 transition-colors hover:text-amber-400"
                            >
                                AGB
                            </Link>
                            <Link href="/impressum" className="text-slate-400 transition-colors hover:text-amber-400">
                                Impressum
                            </Link>
                            <Link href="/datenschutz" className="text-slate-400 transition-colors hover:text-amber-400">
                                Datenschutzerklärung
                            </Link>
                        </nav>
                    </div>
                </div>

                {/* Divider */}
                <div className="my-8 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

                {/* Bottom section */}
                <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                    <p className="flex items-center gap-2 text-sm text-slate-500">
                        <span>© {currentYear ?? '2026'} Stellplatz Zingst</span>
                        <span className="text-slate-700">•</span>
                        <span className="flex items-center gap-1">
                            Made with <FaHeart className="h-3 w-3 text-red-400" /> 
                        </span>
                    </p>

                    <div className="flex items-center gap-4">
                        <span className="text-xs text-slate-500">proudly powered by</span>
                        <Link 
                            href="https://99power.org/de" 
                            target="_blank" 
                            className="group flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-400/10 px-4 py-2 transition-all duration-300 hover:bg-amber-400/20"
                        >
                            <span className="text-2xl leading-none text-amber-400">∞</span>
                            <span className="text-sm font-medium text-amber-400">99power.org</span>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Decorative wave at bottom */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />

            <Suspense>
                <TheTime className='fixed bottom-2 right-2 z-50 rounded-full bg-slate-800/90 px-3 py-1 text-xs text-slate-400 backdrop-blur-sm' />
            </Suspense>
        </footer>
    );
};

export default Footer;
